import React from "react";
import ManagerKPI from "../components/ManagerKPI";
import { Calendar, Clock, Info, TrendingUp } from "lucide-react";

// Helper to format ISO Date to human-readable format
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

// Helper to format ISO Date to human-readable time
const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch (e) {
    return "";
  }
};

const PerformanceSubPage = ({ managerData, kpiData }) => {
  // Determine pulse & text colors based on overall status
  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("green") || s.includes("emerald") || s.includes("success")) {
      return {
        label: "ON TRACK",
        bg: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400",
        pulse: "bg-emerald-500",
        desc: "All tasks are running optimally. You are meeting the targets for this week!"
      };
    }
    if (s.includes("yellow") || s.includes("orange") || s.includes("warning")) {
      return {
        label: "WARNING",
        bg: "bg-yellow-50 dark:bg-yellow-500/10 border-yellow-100 dark:border-yellow-500/20 text-yellow-600 dark:text-yellow-400",
        pulse: "bg-yellow-500",
        desc: "Performance is sub-optimal. Push harder to secure the weekly goals before Monday."
      };
    }
    return {
      label: "ACTION REQUIRED",
      bg: "bg-rose-50 dark:bg-rose-500/10 border-rose-100 dark:border-rose-500/20 text-rose-600 dark:text-rose-400",
      pulse: "bg-rose-500",
      desc: "Overall performance status is RED. Urgent effort needed to reach your target milestone."
    };
  };

  const statusConfig = getStatusConfig(kpiData?.overallStatus);

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Top Banner and Overall Status Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Welcome Banner */}
        <div className="lg:col-span-2 relative overflow-hidden bg-gradient-to-r from-slate-100 to-white dark:from-slate-900 dark:to-[#0B0F19] border border-slate-200 dark:border-slate-900 rounded-3xl p-5 md:p-6 shadow-xl flex flex-col justify-center">
          <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/5 rounded-full blur-[80px] pointer-events-none"></div>
          <div className="relative z-10 space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-sky-500/10 border border-sky-500/20 rounded-full text-sky-600 dark:text-sky-400 text-[9px] font-black uppercase tracking-widest">
              <TrendingUp className="w-2.5 h-2.5" />
              Performance Dashboard
            </div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-800 dark:text-white mt-1.5">
              Welcome back, {managerData.fullName.split(' ')[0]}!
            </h2>
          </div>
        </div>

        {/* Overall Status */}
        <div className={`border rounded-3xl p-5 flex items-center gap-4 transition-all duration-300 ${statusConfig.bg}`}>
          <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 relative">
            <span className={`w-2.5 h-2.5 rounded-full ${statusConfig.pulse} relative`}></span>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Overall Status</p>
            <p className="text-xs md:text-sm font-extrabold mt-1 uppercase tracking-wide">
              {statusConfig.label}
            </p>
          </div>
        </div>
      </div>

      {/* KPI Component */}
      <ManagerKPI kpiData={kpiData} />

      {/* Date Ranges Widget Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#0B0F19] border border-slate-200 dark:border-slate-900 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 bg-sky-500/10 rounded-xl flex items-center justify-center border border-sky-500/20 text-sky-600 dark:text-sky-400">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Week Period</p>
            <p className="text-xs md:text-sm font-extrabold text-slate-800 dark:text-white mt-1">
              {formatDate(kpiData?.weekStart)} – {formatDate(kpiData?.weekEnd)}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-[#0B0F19] border border-slate-200 dark:border-slate-900 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-500/10 rounded-xl flex items-center justify-center border border-indigo-500/20 text-indigo-600 dark:text-indigo-400">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Last Calculated</p>
            <p className="text-xs md:text-sm font-extrabold text-slate-800 dark:text-white mt-1">
              {formatDate(kpiData?.lastCalculatedAt)} at {formatTime(kpiData?.lastCalculatedAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Target Rules Card */}
      <div className="bg-white dark:bg-[#0B0F19] border border-slate-200 dark:border-slate-900 rounded-3xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-48 h-48 bg-sky-500/5 rounded-full blur-[60px] pointer-events-none"></div>
        <h4 className="text-sm font-black text-slate-600 dark:text-slate-300 mb-4 flex items-center gap-2 relative z-10">
          <Info className="w-5 h-5 text-sky-500 dark:text-sky-400" />
          Target Rules Overview
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10 text-xs md:text-sm text-slate-500 dark:text-slate-400">
          <div className="space-y-2 bg-slate-50 dark:bg-slate-950/40 p-4 rounded-xl border border-slate-200 dark:border-slate-900/50">
            <p className="font-extrabold text-slate-700 dark:text-white text-xs">Active Users & Trans</p>
            <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">
              Achievements reset every Monday morning. Transaction targets automatically scale by 100 every week.
            </p>
          </div>
          <div className="space-y-2 bg-slate-50 dark:bg-slate-950/40 p-4 rounded-xl border border-slate-200 dark:border-slate-900/50">
            <p className="font-extrabold text-slate-700 dark:text-white text-xs">API Targets</p>
            <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">
              Resets only on the last day of the month. Increments when a user upgrades and funds their wallet with at least ₦5,000.
            </p>
          </div>
          <div className="space-y-2 bg-slate-50 dark:bg-slate-950/40 p-4 rounded-xl border border-slate-200 dark:border-slate-900/50">
            <p className="font-extrabold text-slate-700 dark:text-white text-xs">Performance Tracking</p>
            <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">
              Counts only from assignment date. Assignments changing will reset tracking to fresh milestone targets.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceSubPage;
